import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Card, Button } from '../UI';
import {
  Package, Plus, FolderPlus, Trash2, Edit2,
  X, ArrowLeft, Search, Check,
} from 'lucide-react';
import { usePantries } from '../../hooks/usePantries';
import { useCareProfiles } from '../../hooks/useCareProfiles';
import ProductCard from '../Products/ProductCard';
import ProductDetailView from '../Products/ProductDetailView';
import AddProductsToPantryModal from './AddProductsToPantryModal';

// ─── Portal Modal ─────────────────────────────────────────────────────────────

const Modal = ({ children, onBackdropClick }) => {
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-lg overflow-y-auto"
      onClick={onBackdropClick}
    >
      <div onClick={e => e.stopPropagation()}>{children}</div>
    </div>,
    document.body
  );
};

// ─── Care Profile Avatar Chip ─────────────────────────────────────────────────

const ProfileChip = ({ profile, size = 'sm' }) => {
  const COLORS = ['#2D5F4D', '#8B6F47', '#06B6D4', '#7C3AED', '#D97706', '#DC2626'];
  const bg = profile.avatarColor || COLORS[(profile.name?.charCodeAt(0) || 0) % COLORS.length];
  const dim = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-caption';
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ring-2 ring-background`}
      style={{ backgroundColor: bg }}
      title={profile.name}
    >
      {(profile.name || '?').slice(0, 1).toUpperCase()}
    </div>
  );
};

// ─── Pantry Card ──────────────────────────────────────────────────────────────

const PantryCard = ({ pantry, careProfiles, onClick, onDelete }) => {
  const tagged = careProfiles.filter(p => (pantry.careProfileIds || []).includes(p.id));
  const count  = pantry.items?.length || 0;

  return (
    <div className="group relative cursor-pointer" onClick={onClick}>
      {/* Depth shadow */}
      <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-xl bg-black/15 blur-sm transition-all duration-300 group-hover:translate-x-3 group-hover:translate-y-3 group-hover:blur-md" />

      <div className="relative rounded-xl overflow-hidden border border-black/8 bg-white transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl">

        {/* Cover area */}
        <div className="relative h-40 overflow-hidden bg-gradient-to-br from-primary/12 via-primary/6 to-accent/8">
          <div className="w-full h-full flex flex-col items-center justify-center gap-sm">
            <Package size={44} className="text-primary/20" />
            <p className="text-caption text-primary/25 font-medium">
              {count > 0 ? `${count} product${count !== 1 ? 's' : ''}` : 'No products yet'}
            </p>
          </div>

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

          {/* Delete btn */}
          <button
            onClick={e => { e.stopPropagation(); onDelete(e); }}
            className="absolute top-sm left-sm w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-error/80 z-10"
          >
            <Trash2 size={13} className="text-white" />
          </button>

          {/* Product count badge */}
          <div className="absolute bottom-sm left-sm">
            <span className="px-sm py-xs rounded-full bg-black/50 backdrop-blur-sm text-white text-[11px] font-semibold flex items-center gap-xs">
              <Package size={10} />
              {count} product{count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Info section */}
        <div className="p-md">
          <h3 className="text-callout font-bold text-text-primary truncate">{pantry.name}</h3>

          {pantry.description ? (
            <p className="text-caption text-text-secondary line-clamp-2 mt-xs leading-relaxed">{pantry.description}</p>
          ) : (
            <p className="text-caption text-text-tertiary/40 italic mt-xs">No description</p>
          )}

          {tagged.length > 0 && (
            <div className="flex items-center gap-xs mt-sm">
              <span className="text-[11px] text-text-tertiary">For</span>
              <div className="flex -space-x-1.5">
                {tagged.slice(0, 5).map(p => <ProfileChip key={p.id} profile={p} size="sm" />)}
                {tagged.length > 5 && (
                  <div className="w-6 h-6 rounded-full bg-text-tertiary/15 flex items-center justify-center text-[9px] font-bold text-text-secondary ring-2 ring-background">
                    +{tagged.length - 5}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Spine accent */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-primary/50 via-primary/25 to-primary/50 rounded-l-xl" />
      </div>
    </div>
  );
};

// ─── Pantry Detail View ───────────────────────────────────────────────────────

const PantryDetailView = ({ pantryData, onBack, careProfiles, pantries, hooks }) => {
  const {
    removeItemFromPantry, addItemsToPantry, renamePantry,
    updateDescription, updatePantryCareProfiles, deletePantry
  } = hooks;

  const [showAddProductsModal, setShowAddProductsModal] = useState(false);
  const [showEditModal,        setShowEditModal]        = useState(false);
  const [showDeleteConfirm,    setShowDeleteConfirm]    = useState(false);
  const [deleting,             setDeleting]             = useState(false);
  const [saving,               setSaving]               = useState(false);
  const [searchQuery,          setSearchQuery]          = useState('');
  const [selectedProduct,      setSelectedProduct]      = useState(null);

  const [editName,        setEditName]        = useState(pantryData.name);
  const [editDesc,        setEditDesc]        = useState(pantryData.description || '');
  const [editCareIds,     setEditCareIds]     = useState(pantryData.careProfileIds || []);

  const livePantry = pantries.find(p => p.id === pantryData.id) || pantryData;
  const currentItems = livePantry.items || [];
  const filteredItems = searchQuery
    ? currentItems.filter(item =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.brand?.toLowerCase().includes(searchQuery.toLowerCase()))
    : currentItems;

  const tagged = careProfiles.filter(p => (livePantry.careProfileIds || []).includes(p.id));

  const handleRemoveItem = async (productId) => {
    try { await removeItemFromPantry(livePantry.id, productId); }
    catch (err) { alert('Failed to remove item: ' + err.message); }
  };

  const handleAddProducts = async (products) => {
    try {
      const items = products.map(p => ({
        productId: p.id, name: p.name, brand: p.brand || '',
        imageUrl: p.imageUrl || p.imageURL || '', category: p.category || '', addedAt: new Date(),
      }));
      if (addItemsToPantry) await addItemsToPantry(livePantry.id, items);
      setShowAddProductsModal(false);
    } catch (err) { alert('Failed to add products: ' + err.message); }
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await renamePantry(livePantry.id, editName.trim());
      await updateDescription(livePantry.id, editDesc.trim());
      await updatePantryCareProfiles(livePantry.id, editCareIds);
      setShowEditModal(false);
    } catch (err) { alert('Failed to save: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await deletePantry(livePantry.id); onBack(); }
    catch (err) { alert('Failed to delete: ' + err.message); setDeleting(false); }
  };

  if (selectedProduct) {
    return <ProductDetailView product={selectedProduct} onClose={() => setSelectedProduct(null)} />;
  }

  return (
    <div className="space-y-lg">

      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-sm text-text-secondary hover:text-primary transition-colors">
          <ArrowLeft size={20} /><span className="text-callout font-semibold">Back</span>
        </button>
        <div className="flex gap-sm">
          <button onClick={() => setShowEditModal(true)} className="w-9 h-9 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:border-primary/40 transition-colors">
            <Edit2 size={15} className="text-text-secondary" />
          </button>
          <button onClick={() => setShowDeleteConfirm(true)} className="w-9 h-9 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:border-error/40 transition-colors">
            <Trash2 size={15} className="text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Pantry header */}
      <div className="flex items-start gap-lg">
        <div className="flex-1 min-w-0">
          <h1 className="text-display font-rounded text-primary">{livePantry.name}</h1>
          {livePantry.description && <p className="text-body text-text-secondary mt-xs">{livePantry.description}</p>}
          <p className="text-caption text-text-tertiary mt-sm">
            {currentItems.length} product{currentItems.length !== 1 ? 's' : ''}
          </p>
        </div>
        {tagged.length > 0 && (
          <div className="flex items-center gap-sm flex-shrink-0">
            <span className="text-caption text-text-tertiary">For</span>
            <div className="flex -space-x-1.5">
              {tagged.slice(0, 5).map(p => <ProfileChip key={p.id} profile={p} size="md" />)}
            </div>
          </div>
        )}
      </div>

      {/* Products header */}
      <div className="flex items-center justify-between">
        <h2 className="text-title-3 font-semibold text-text-primary">Products</h2>
        <Button variant="primary" size="small" onClick={() => setShowAddProductsModal(true)}>
          <Plus size={14} className="mr-xs" />Add
        </Button>
      </div>

      {/* Search */}
      {currentItems.length > 0 && (
        <div className="relative">
          <Search size={16} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search products…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-xxxl pr-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
          />
        </div>
      )}

      {/* Products grid */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-xxxl border-2 border-dashed border-text-tertiary/15 rounded-large">
          <Package size={40} className="text-text-tertiary/25 mx-auto mb-md" />
          <p className="text-callout font-semibold text-text-secondary mb-xs">
            {searchQuery ? `No results for "${searchQuery}"` : 'No products yet'}
          </p>
          {!searchQuery && (
            <p className="text-caption text-text-tertiary mb-lg">Add products from the catalogue to this pantry.</p>
          )}
          {!searchQuery && (
            <Button variant="primary" onClick={() => setShowAddProductsModal(true)}>
              <Plus size={16} className="mr-xs" />Add Products
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-md">
          {filteredItems.map(item => (
            <ProductCard
              key={item.productId || item.id}
              product={item}
              onClick={() => setSelectedProduct(item)}
              showRemove
              onRemove={() => handleRemoveItem(item.productId || item.id)}
            />
          ))}
        </div>
      )}

      {/* Add Products Modal */}
      {showAddProductsModal && (
        <AddProductsToPantryModal
          pantry={livePantry}
          onClose={() => setShowAddProductsModal(false)}
          onAddProducts={handleAddProducts}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <Modal onBackdropClick={() => setShowEditModal(false)}>
          <div className="bg-background rounded-large border border-text-tertiary/15 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-xl pt-xl pb-lg border-b border-text-tertiary/10">
              <h3 className="text-title-3 font-semibold text-text-primary">Edit Pantry</h3>
              <button onClick={() => setShowEditModal(false)} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center transition-colors">
                <X size={18} className="text-text-tertiary" />
              </button>
            </div>
            <div className="p-xl space-y-lg">
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  autoFocus
                  className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                />
              </div>
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">
                  Description <span className="font-normal normal-case text-text-tertiary">(optional)</span>
                </label>
                <textarea
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  rows={2}
                  maxLength={200}
                  className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body resize-none"
                />
              </div>
              {careProfiles.length > 0 && (
                <div className="space-y-sm">
                  <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">For Care Profiles</label>
                  <div className="flex flex-wrap gap-sm">
                    {careProfiles.map(p => {
                      const sel = editCareIds.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          onClick={() => setEditCareIds(prev => sel ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                          className={`flex items-center gap-sm px-md py-sm rounded-full border text-caption font-semibold transition-all ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-text-tertiary/20 text-text-secondary hover:border-primary/30'}`}
                        >
                          <ProfileChip profile={p} size="sm" />{p.name}{sel && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-sm px-xl pb-xl">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">Cancel</Button>
              <Button variant="primary" onClick={handleSaveEdit} disabled={!editName.trim() || saving} className="flex-1">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <Modal onBackdropClick={() => setShowDeleteConfirm(false)}>
          <Card className="max-w-sm w-full text-center">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-md">
              <Trash2 size={24} className="text-error" />
            </div>
            <h3 className="text-title-3 font-bold text-text-primary mb-sm">Delete Pantry?</h3>
            <p className="text-body text-text-secondary mb-lg">"{livePantry.name}" and all its products will be removed.</p>
            <div className="flex gap-sm">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</Button>
              <Button variant="primary" className="flex-1 bg-error border-error hover:bg-error/90" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </Card>
        </Modal>
      )}
    </div>
  );
};

// ─── Main PantryHub ───────────────────────────────────────────────────────────

const PantryHub = () => {
  const {
    pantries, loading, createPantry, deletePantry, renamePantry,
    updatePantryCareProfiles, updateDescription, removeItemFromPantry, addItemsToPantry
  } = usePantries();
  const { careProfiles = [] } = useCareProfiles();

  const [selectedPantry, setSelectedPantry] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName,     setNewName]     = useState('');
  const [newDesc,     setNewDesc]     = useState('');
  const [newCareIds,  setNewCareIds]  = useState([]);
  const [creating,    setCreating]    = useState(false);

  const resetModal = () => { setNewName(''); setNewDesc(''); setNewCareIds([]); setShowCreateModal(false); };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const newPantry = await createPantry(newName.trim(), newDesc.trim(), newCareIds);
      resetModal();
      if (newPantry) setSelectedPantry(newPantry);
    } catch { alert('Failed to create pantry'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this pantry? This cannot be undone.')) return;
    try { await deletePantry(id); } catch { alert('Failed to delete'); }
  };

  const hooks = { removeItemFromPantry, addItemsToPantry, renamePantry, updateDescription, updatePantryCareProfiles, deletePantry };

  if (selectedPantry) {
    const current = pantries.find(p => p.id === selectedPantry.id) || selectedPantry;
    return (
      <PantryDetailView
        pantryData={current}
        onBack={() => setSelectedPantry(null)}
        careProfiles={careProfiles}
        pantries={pantries}
        hooks={hooks}
      />
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center py-xxxl"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-xl">

      {/* Header */}
      <div className="flex items-end justify-between gap-md flex-wrap">
        <div>
          <h1 className="text-display font-rounded text-primary">My Pantries</h1>
          <p className="text-body text-text-secondary mt-xs">Your verified products, organised into pantries.</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <FolderPlus size={16} className="mr-xs" />New Pantry
        </Button>
      </div>

      {/* Pantry grid */}
      {pantries.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-xl pt-sm">
          {pantries.map(pantry => (
            <PantryCard
              key={pantry.id}
              pantry={pantry}
              careProfiles={careProfiles}
              onClick={() => setSelectedPantry(pantry)}
              onDelete={(e) => handleDelete(e, pantry.id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-xxxl border-2 border-dashed border-text-tertiary/15 rounded-large">
          <Package size={40} className="text-text-tertiary/25 mx-auto mb-md" />
          <p className="text-callout font-semibold text-text-secondary mb-xs">No pantries yet</p>
          <p className="text-caption text-text-tertiary mb-lg">Create one to organise your verified products.</p>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <FolderPlus size={16} className="mr-xs" />Create Pantry
          </Button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal onBackdropClick={resetModal}>
          <div className="bg-background rounded-large border border-text-tertiary/15 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-xl pt-xl pb-lg border-b border-text-tertiary/10">
              <h3 className="text-title-3 font-semibold text-text-primary">New Pantry</h3>
              <button onClick={resetModal} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center transition-colors">
                <X size={18} className="text-text-tertiary" />
              </button>
            </div>
            <div className="p-xl space-y-lg">
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Kitchen Staples"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body placeholder:text-text-tertiary/50"
                />
              </div>
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">
                  Description <span className="font-normal normal-case text-text-tertiary">(optional)</span>
                </label>
                <textarea
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  placeholder="What goes in this pantry?"
                  rows={2}
                  maxLength={200}
                  className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body resize-none placeholder:text-text-tertiary/50"
                />
              </div>
              {careProfiles.length > 0 && (
                <div className="space-y-sm">
                  <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">
                    For Care Profiles <span className="font-normal normal-case text-text-tertiary">(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-sm">
                    {careProfiles.map(p => {
                      const sel = newCareIds.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          onClick={() => setNewCareIds(prev => sel ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                          className={`flex items-center gap-sm px-md py-sm rounded-full border text-caption font-semibold transition-all ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-text-tertiary/20 text-text-secondary hover:border-primary/30'}`}
                        >
                          <ProfileChip profile={p} size="sm" />{p.name}{sel && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-sm px-xl pb-xl">
              <Button variant="outline" onClick={resetModal} className="flex-1">Cancel</Button>
              <Button variant="primary" onClick={handleCreate} disabled={!newName.trim() || creating} className="flex-1">
                {creating ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PantryHub;